
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';

export interface AuthContextType {
  session: Session | null;
  user: User | null;
  editingUserId: string | null;
  loading: boolean;
  isAuthLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any | null }>;
  signUp: (email: string, password: string) => Promise<{ error: any | null, data: any | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any | null }>;
  isEditingAsDifferentUser: () => boolean;
  clearEditingState: () => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);

  useEffect(() => {
    console.log("Setting up AuthProvider");
    
    // Check if super user is editing as someone else
    const storedEditUserId = localStorage.getItem('superUserEditingFor');
    if (storedEditUserId) {
      setEditingUserId(storedEditUserId);
    }

    // First set up the auth listener to handle auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log("Auth state changed in AuthContext:", event, session?.user?.id);
      
      if (event === 'SIGNED_OUT') {
        // Clear all states when signed out
        setSession(null);
        setUser(null);
        clearEditingState();
      } else {
        setSession(session);
        setUser(session?.user ?? null);
      }
      
      setLoading(false);
    });

    // Then get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log("Initial session in AuthContext:", session?.user?.id);
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Function to determine if super user is editing as different user
  const isEditingAsDifferentUser = () => {
    return !!editingUserId && editingUserId !== user?.id;
  };

  // Function to clear editing state
  const clearEditingState = () => {
    localStorage.removeItem('superUserEditingFor');
    setEditingUserId(null);
  };

  // Authentication functions
  const signIn = async (email: string, password: string) => {
    console.log("Signing in with email:", email);
    setIsAuthLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        console.error("Sign in error:", error);
        toast({
          variant: "destructive",
          title: "Login Failed",
          description: error.message || "Invalid credentials"
        });
        setIsAuthLoading(false);
        return { error };
      }
      
      console.log("Sign in successful:", data.user?.id);
      setIsAuthLoading(false);
      return { error: null };
    } catch (err) {
      console.error("Unexpected error during sign in:", err);
      setIsAuthLoading(false);
      return { error: err };
    }
  };

  const signUp = async (email: string, password: string) => {
    setIsAuthLoading(true);
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });
    setIsAuthLoading(false);
    return { data, error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (!error) {
      // Clear any editing state when signing out
      clearEditingState();
    } else {
      console.error("Error during sign out:", error);
    }
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email);
    return { error };
  };

  const value = {
    session,
    user,
    editingUserId,
    loading,
    isAuthLoading,
    signIn,
    signUp,
    signOut,
    resetPassword,
    isEditingAsDifferentUser,
    clearEditingState
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
